import * as React from "react";
import {
  AgentTokenService,
  PluginPackageUnavailableError,
  type AgentTokenListItem
} from "../../../../../services/agent-token-service";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaModalIconWrapper from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { type KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import {
  formatAgentTokenDate,
  isAgentTokenUsable,
  MAX_ACTIVE_TOKENS,
  PLUGIN_DOWNLOAD_FILE_NAME,
  PLUGIN_DOWNLOAD_URL,
  resolveAgentTokenStatus,
  sortAgentTokens,
  summarizeAgentTokens,
  type AgentTokenStatusView,
  type AgentTokenSummary
} from "../agent-token-utils";
import { extractBackendError } from "../ai-chatbot-utils";
import AgentTokenCreateModal, { describeCreateError } from "./agent-token-create-modal";
import "./agent-tokens-tab.scss";

// ── Agent Tokens tab (remote MCP) ────────────────────────────────────────────
// Every user manages THEIR OWN tokens here — the server scopes list/revoke to
// the verified caller, so this is not an owner-only surface. The token string
// is only ever seen inside the create modal; this list is metadata.

interface State {
  tokens: AgentTokenListItem[];
  loading: boolean;
  loadError: string | null;
  showCreate: boolean;
  revokeTarget: AgentTokenListItem | null;
  revoking: boolean;
  revokeError: string | null;
  downloading: boolean;
  downloadError: string | null;
}

class AgentTokensTab extends React.PureComponent<{}, State> {
  state: State = {
    tokens: [],
    loading: true,
    loadError: null,
    showCreate: false,
    revokeTarget: null,
    revoking: false,
    revokeError: null,
    downloading: false,
    downloadError: null
  };

  private isMounted_: boolean = false;

  componentDidMount(): void {
    this.isMounted_ = true;
    void this.loadTokens();
  }

  componentWillUnmount(): void {
    this.isMounted_ = false;
  }

  private loadTokens = async (): Promise<void> => {
    try {
      const tokens: AgentTokenListItem[] = (await AgentTokenService.List()).data.tokens ?? [];
      if (!this.isMounted_) return;
      this.setState({ tokens: sortAgentTokens(tokens, Date.now()), loading: false, loadError: null });
    } catch (err: unknown) {
      if (!this.isMounted_) return;
      this.setState({ loading: false, loadError: describeCreateError(err) });
    }
  };

  private handleRevoke = async (): Promise<void> => {
    const target: AgentTokenListItem | null = this.state.revokeTarget;
    if (!target || this.state.revoking) return;
    this.setState({ revoking: true, revokeError: null });
    try {
      await AgentTokenService.Revoke(target.tokenId);
      if (!this.isMounted_) return;
      this.setState({ revoking: false, revokeTarget: null });
      await this.loadTokens();
    } catch (err: unknown) {
      if (!this.isMounted_) return;
      this.setState({ revoking: false, revokeError: extractBackendError(err) });
    }
  };

  /** The table's default cell color is too dim in the dark theme for the dates a user scans this list for. */
  private renderDateCell(text: string): React.ReactNode {
    return <span className="aichat-agent-tokens__date">{text}</span>;
  }

  private buildColumns(): KuikaTableColumn<AgentTokenListItem>[] {
    const now: number = Date.now();
    return [
      {
        id: "label",
        header: (): React.ReactNode => "Name",
        cell: (row: AgentTokenListItem): React.ReactNode => (
          <span className="aichat-agent-tokens__name">
            {row.label || <span className="aichat-agent-tokens__name--unnamed">Unnamed token</span>}
          </span>
        )
      },
      {
        id: "status",
        header: (): React.ReactNode => "Status",
        width: 140,
        cell: (row: AgentTokenListItem): React.ReactNode => {
          const view: AgentTokenStatusView = resolveAgentTokenStatus(row, now);
          return <KuikaBadge type="label" size="small" color={view.color} content={view.label} />;
        }
      },
      {
        id: "createdAt",
        header: (): React.ReactNode => "Created",
        width: 130,
        noWrap: true,
        cell: (row: AgentTokenListItem): React.ReactNode => this.renderDateCell(formatAgentTokenDate(row.createdAt))
      },
      {
        id: "lastUsedAt",
        header: (): React.ReactNode => "Last used",
        width: 130,
        noWrap: true,
        cell: (row: AgentTokenListItem): React.ReactNode =>
          this.renderDateCell(row.lastUsedAt ? formatAgentTokenDate(row.lastUsedAt) : "Never")
      },
      {
        id: "expiresAt",
        header: (): React.ReactNode => "Expires",
        width: 130,
        noWrap: true,
        cell: (row: AgentTokenListItem): React.ReactNode => this.renderDateCell(formatAgentTokenDate(row.expiresAt))
      },
      {
        id: "actions",
        header: (): React.ReactNode => "",
        width: 110,
        align: "right",
        cell: (row: AgentTokenListItem): React.ReactNode =>
          isAgentTokenUsable(row, now) ? (
            <KuikaButton
              type="text"
              size="sm"
              color="danger"
              onClick={(): void => this.setState({ revokeTarget: row, revokeError: null })}
              data-testid={`agentTokenRevoke-${row.tokenId}`}
            >
              Revoke
            </KuikaButton>
          ) : null
      }
    ];
  }

  private renderRevokeModal(target: AgentTokenListItem): React.ReactNode {
    const { revoking, revokeError } = this.state;
    const close = (): void => this.setState({ revokeTarget: null, revokeError: null });
    const title: string = "Revoke agent token";
    return (
      <KModal
        showModal
        header={title}
        width={480}
        centered
        closeModal={revoking ? undefined : close}
        footer={
          <div className="aichat-agent-tokens__modal-footer">
            <KuikaButton type="secondary" size="md" disabled={revoking} onClick={close}>
              Cancel
            </KuikaButton>
            <KuikaButton
              type="primary"
              color="danger"
              size="md"
              loading={revoking}
              disabled={revoking}
              onClick={this.handleRevoke}
              data-testid="agentTokenRevokeConfirm"
            >
              Revoke token
            </KuikaButton>
          </div>
        }
      >
        <div className="aichat-agent-tokens__modal" data-testid="agentTokenRevokeModal">
          <div className="aichat-agent-tokens__modal-header">
            <KuikaModalIconWrapper iconName="key-off" intent="destructive" />
            <KuikaH5>{title}</KuikaH5>
          </div>
          <p className="aichat-agent-tokens__modal-hint">
            <strong>{target.label || "This token"}</strong> stops working immediately — including in any Claude Code
            session that is using it right now. This cannot be undone; create a new token to reconnect.
          </p>
          {revokeError && (
            <div className="aichat-agent-tokens__modal-error" role="alert">
              {revokeError}
            </div>
          )}
        </div>
      </KModal>
    );
  }

  /** Verified download: the bytes are checked to be a zip before the browser is asked to save them. */
  private handleDownloadPlugin = async (): Promise<void> => {
    if (this.state.downloading) return;
    this.setState({ downloading: true, downloadError: null });
    let objectUrl: string | null = null;
    try {
      const archive: Blob = await AgentTokenService.DownloadPlugin(PLUGIN_DOWNLOAD_URL);
      objectUrl = URL.createObjectURL(archive);
      const link: HTMLAnchorElement = document.createElement("a");
      link.href = objectUrl;
      link.download = PLUGIN_DOWNLOAD_FILE_NAME;
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (this.isMounted_) this.setState({ downloading: false });
    } catch (err: unknown) {
      if (!this.isMounted_) return;
      const downloadError: string =
        err instanceof PluginPackageUnavailableError
          ? "The plugin package is not available on this Kuika server yet. Please contact your administrator."
          : "The plugin could not be downloaded. Check your connection and try again.";
      this.setState({ downloading: false, downloadError });
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  };

  private renderSetupCard(summary: AgentTokenSummary): React.ReactNode {
    const atLimit: boolean = summary.activeCount >= MAX_ACTIVE_TOKENS;
    return (
      <section className="aichat-agent-tokens__setup" aria-label="Connect Claude Code">
        <div className="aichat-agent-tokens__setup-hero">
          <div className="aichat-agent-tokens__setup-hero-left">
            <span className="aichat-agent-tokens__pill">Claude Code</span>
            <div className="aichat-agent-tokens__setup-title">Build Kuika apps from Claude Code</div>
            <div className="aichat-agent-tokens__setup-sub">
              Claude Code works in your own open designer session, with your permissions — nothing runs on your machine
              besides Claude Code.
            </div>
          </div>
          <div className="aichat-agent-tokens__stats">
            <div className="aichat-agent-tokens__stat">
              <span className="aichat-agent-tokens__stat-label">Active tokens</span>
              <span className="aichat-agent-tokens__stat-value" data-testid="agentTokenActiveCount">
                {summary.activeCount}
                <span className="aichat-agent-tokens__stat-of"> / {MAX_ACTIVE_TOKENS}</span>
              </span>
            </div>
            <div className="aichat-agent-tokens__stat">
              <span className="aichat-agent-tokens__stat-label">Next expiry</span>
              <span className="aichat-agent-tokens__stat-value aichat-agent-tokens__stat-value--text">
                {summary.nextExpiryAt ? formatAgentTokenDate(summary.nextExpiryAt) : "—"}
              </span>
            </div>
          </div>
        </div>

        <ol className="aichat-agent-tokens__steps">
          <li className="aichat-agent-tokens__step">
            <span className="aichat-agent-tokens__step-index">1</span>
            <div className="aichat-agent-tokens__step-title">Install the plugin</div>
            <p className="aichat-agent-tokens__step-text">
              Skills that teach Claude how to work in Kuika, plus a guard that asks before anything destructive. Unzip
              it and follow the README inside.
            </p>
            <KuikaButton
              type="secondary"
              size="md"
              onClick={this.handleDownloadPlugin}
              withIconButton={<KuikaSvg width={16} height={16} name="download" type="common" />}
              loading={this.state.downloading}
              disabled={this.state.downloading}
              data-testid="agentTokenDownloadPlugin"
            >
              Download plugin
            </KuikaButton>
            {this.state.downloadError && (
              <p className="aichat-agent-tokens__step-note" role="alert">
                {this.state.downloadError}
              </p>
            )}
          </li>
          <li className="aichat-agent-tokens__step">
            <span className="aichat-agent-tokens__step-index">2</span>
            <div className="aichat-agent-tokens__step-title">Create a token</div>
            <p className="aichat-agent-tokens__step-text">
              One per device. It acts <strong>as you</strong>, expires after 90 days, and you can revoke it at any time.
            </p>
            <KuikaButton
              type="primary"
              size="md"
              disabled={atLimit}
              onClick={(): void => this.setState({ showCreate: true })}
              withIconButton={<KuikaSvg width={16} height={16} name="plus" type="common" />}
              data-testid="agentTokenCreate"
            >
              Create token
            </KuikaButton>
            {atLimit && (
              <p className="aichat-agent-tokens__step-note">
                You have {MAX_ACTIVE_TOKENS} active tokens — revoke one to create another.
              </p>
            )}
          </li>
          <li className="aichat-agent-tokens__step">
            <span className="aichat-agent-tokens__step-index">3</span>
            <div className="aichat-agent-tokens__step-title">Run the command</div>
            <p className="aichat-agent-tokens__step-text">
              Paste the command you get with the token into a terminal and start Claude Code. With your project open
              here, run <code className="aichat-agent-tokens__code">/kuika-agentic-dev:connect</code>.
            </p>
          </li>
        </ol>
      </section>
    );
  }

  render(): React.ReactNode {
    const { tokens, loading, loadError, showCreate, revokeTarget } = this.state;
    const summary: AgentTokenSummary = summarizeAgentTokens(tokens, Date.now());

    return (
      <div className="aichat-agent-tokens" data-testid="agentTokensTab">
        <p className="aichat-agent-tokens__intro">
          Agent tokens connect Claude Code to Kuika. A token acts <strong>as you</strong>, only in your own open
          designer session — it can never reach another member's work.
        </p>

        {this.renderSetupCard(summary)}

        <div className="aichat-agent-tokens__list-header">
          <h3 className="aichat-agent-tokens__list-title">
            Your tokens
            {!loading && !loadError && <span className="aichat-agent-tokens__list-count">{tokens.length}</span>}
          </h3>
          <span className="aichat-agent-tokens__list-hint">Only you can see and revoke these.</span>
        </div>

        {loadError ? (
          <div className="aichat-agent-tokens__error" role="alert">
            <span className="aichat-agent-tokens__error-icon">
              <KuikaSvg width={20} height={20} name="circle-warning" type="common" />
            </span>
            <div>
              <div className="aichat-agent-tokens__error-title">Agent tokens are unavailable</div>
              <div className="aichat-agent-tokens__error-body">{loadError}</div>
            </div>
          </div>
        ) : (
          <KuikaTable<AgentTokenListItem>
            data={tokens}
            columns={this.buildColumns()}
            rowKey={(row: AgentTokenListItem): string => row.tokenId}
            loading={loading}
            loadingVariant="skeleton"
            skeletonRowCount={3}
            enablePagination={false}
            fitContentHeight
            emptyIcon={<KuikaSvg width={32} height={32} name="key" type="common" />}
            emptyTitle="No agent tokens yet"
            emptyDescription="Create a token to connect Claude Code to your Kuika workspace."
            data-testid="agentTokensTable"
          />
        )}

        <p className="aichat-agent-tokens__footnote">
          Kuika never stores a token — only when it was created, last used and when it expires. Revoking takes effect
          immediately, including in a Claude Code session that is running right now.
        </p>

        {showCreate && (
          <AgentTokenCreateModal
            onClose={(): void => this.setState({ showCreate: false })}
            onCreated={this.loadTokens}
          />
        )}
        {revokeTarget && this.renderRevokeModal(revokeTarget)}
      </div>
    );
  }
}

export default AgentTokensTab;
